<template>
    <component :disabled="props.disabled" :is="props.as" :class="buttonClass">
        <icon v-if="props.leftIcon" :icon="props.leftIcon" :class="[
            'tw-text-xl',
            props.iconClass,
            props.loading && 'tw-invisible'
        ]" />

        <span v-if="!props.icon" class="tw-whitespace-normal" :class="[
            props.loading && 'tw-invisible'
        ]">
            <slot />
        </span>

        <icon v-if="props.icon" :icon="props.icon" :class="[
            'tw-text-xl',
            props.iconClass,
            props.loading && 'tw-invisible'
        ]" />

        <icon v-if="props.rightIcon" :icon="props.rightIcon" :class="[
            'tw-text-xl',
            props.iconClass,
            props.loading && 'tw-invisible'
        ]" />

        <icon v-if="props.loading" class="tw-absolute tw-left-1/2 tw-text-xl -tw-translate-x-1/2" icon="line-md:loading-twotone-loop" />
    </component>
</template>
<script setup>
import { defineProps, computed } from 'vue';
import { cva } from 'class-variance-authority';

const props = defineProps({
    leftIcon: String,
    rightIcon: String,
    loading: Boolean,
    disabled: Boolean,
    full: Boolean,
    icon: String,
    iconClass: String,

    as: {
        type: [String, Object],
        default: 'button'
    },

    intent: {
        type: String,
        default: 'primary',
        validator: (v) => {
            return ['primary', 'secondary', 'danger', 'text', 'simple', 'info', 'warning', 'icon', 'flat', 'outline', 'ghost', 'success', 'light', 'gradient', 'soft', 'link', 'neutral', 'white', 'black', 'transparent', 'glass', 'pill'].includes(v)
        }
    }
});

const buttonClass = computed(() => {
    return cva("tw-relative tw-inline-flex tw-w-fit tw-border tw-cursor-pointer active:tw-translate-y-0.5x active:tw-border-black/50 active:tw-shadow-none active:!tw-border-bx tw-border-solid tw-border-b-2x tw-items-center tw-justify-center tw-duration-200  tw-text-sm tw-rounded tw-min-h-[40px] tw-px-3 tw-py-0.5 tw-font-semibold tw-gap-2",
    {
        variants: {
            intent: {
                primary: "tw-bg-primary-500 tw-text-white  tw-border-primary-600 hover:tw-bg-primary-400 hover:tw-border-primary-500",
                secondary: "tw-bg-primary-100 tw-text-primary-600  hover:tw-text-primary-700  tw-border-solid tw-border tw-border-primary-200",
                dark: "tw-bg-gray-100 tw-text-gray-600  tw-border-gray-200",
                danger: "tw-bg-rose-500 tw-text-white  tw-border-rose-600 hover:tw-bg-rose-400 hover:tw-border-rose-500",
                text: "tw-text-primary-500 tw-border-transparent hover:tw-border-primary-200",
                simple: "tw-text-neutral-700 hover:tw-text-primary-700 hover:tw-border-primary-400 tw-bg-white",
                warning: "tw-bg-amber-500 tw-text-white  !tw-border-amber-600",
                info: "tw-bg-cyan-500 tw-text-white  !tw-border-cyan-600",
                purple: "tw-bg-purple-500 tw-text-white  !tw-border-purple-600",
                icon: "tw-text-neutral-700 hover:tw-text-neutral-900 hover:tw-border-gray-300 tw-bg-white !tw-px-1.5 !tw-py-1.5 tw-min-h-[30px] tw-aspect-square",
                flat: "tw-bg-primary-500 tw-text-white tw-border-transparent hover:tw-bg-primary-600 active:tw-shadow-none",
                outline: "tw-bg-transparent tw-text-primary-600 tw-border-primary-500 hover:tw-bg-primary-50 hover:tw-border-primary-600",
                ghost: "tw-bg-transparent tw-text-primary-600 tw-border-transparent hover:tw-bg-primary-50 hover:tw-text-primary-700",
                success: "tw-bg-emerald-500 tw-text-white tw-border-emerald-600 hover:tw-bg-emerald-400 hover:tw-border-emerald-500",
                light: "tw-bg-gray-50 tw-text-gray-700 tw-border-gray-200 hover:tw-bg-gray-100 hover:tw-border-gray-300",
                gradient: "tw-bg-gradient-to-r tw-from-primary-500 tw-to-primary-600 tw-text-white tw-border tw-border-primary-600 !tw-border-b tw-border-b-primary-700 hover:tw-from-primary-600 hover:tw-to-primary-700  tw-overflow-hidden",
                soft: "tw-bg-primary-50 tw-text-primary-700 tw-border-primary-200 hover:tw-bg-primary-100 hover:tw-text-primary-800",
                link: "tw-bg-transparent tw-text-primary-600 tw-border-transparent hover:tw-text-primary-800 hover:tw-underline tw-shadow-none",
                neutral: "tw-bg-gray-500 tw-text-white tw-border-gray-600 hover:tw-bg-gray-400 hover:tw-border-gray-500",
                white: "tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-bg-gray-50 hover:tw-border-gray-400",
                black: "tw-bg-black tw-text-white tw-border-gray-800 hover:tw-bg-gray-800 hover:tw-border-gray-700",
                transparent: "tw-bg-transparent tw-text-gray-600 tw-border-transparent hover:tw-bg-gray-50 hover:tw-text-gray-800",
                glass: "tw-bg-white/20 tw-backdrop-blur-sm tw-text-white tw-border-white/30 hover:tw-bg-white/30 hover:tw-border-white/50",
                pill: "tw-bg-primary-500 tw-text-white tw-border-primary-600 hover:tw-bg-primary-400 hover:tw-border-primary-500 tw-rounded-full",
            },
            disabled: {
                true: "!tw-bg-gray-100 !tw-text-gray-500  !tw-cursor-not-allowed !tw-border-gray-300 tw-opacity-75 !tw-shadow-none active:tw-border-b-2x active:!tw-translate-y-0"
            },
            full: {
                true: "!tw-w-full"
            }
        }
    })({ intent: props.intent, disabled: props.disabled, full: props.full });
});

</script>
<style></style>
